import {
  useXeroAccountCodeMappings,
  useXeroAccountCodeMappingsMutation,
} from '@omnipresentgroup/domains';
import type {
  XeroAccountCodeMappingErrorItem,
  XeroAccountCodeMappingItem,
} from '@omnipresentgroup/types';
import compare from 'just-compare';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { XeroAccountCodeMappingExtendedItem } from '../types';
import type { TableMetaType } from './useAccountCodesTableConfig';

const TAX_RATE_MISMATCH_WARNING = 'Tax rate mismatch';

/**
 * Type guard for XeroAccountCodeMappingItem
 * @param item
 * @returns boolean indicating if the item is a valid XeroAccountCodeMappingItem
 */
const isValidMappingItem = (item: unknown): boolean =>
  item !== null &&
  typeof item === 'object' &&
  'category' in item &&
  typeof (item as XeroAccountCodeMappingItem).category?.id === 'string';

/**
 * Transform the already updated account code mappings to handle the possible error messages
 * @param previousItems previous xero account code mappings
 * @param newItems updated xero account code mappings
 * @returns updated xero account code mappings with error messages
 */
const transformUpdatedAccountCodeMappings = (
  previousItems: XeroAccountCodeMappingItem[],
  newItems: unknown[],
): XeroAccountCodeMappingItem[] => {
  return newItems
    .map((item): XeroAccountCodeMappingItem | null => {
      // Guard for valid item type
      if (!item || typeof item !== 'object') {
        return null;
      }

      // Handle successful mapping item
      if (!('error' in item)) {
        return isValidMappingItem(item)
          ? (item as XeroAccountCodeMappingItem)
          : null;
      }

      // Handle error item
      const errorItem = item as XeroAccountCodeMappingErrorItem;
      if (!errorItem.categoryId) {
        return null;
      }

      const existingItem = previousItems.find(
        (previous) => previous.category.id === errorItem.categoryId,
      );

      return existingItem ? { ...existingItem, error: errorItem.error } : null;
    })
    .filter((item): item is XeroAccountCodeMappingItem => item !== null);
};

export const useAccountCodeMappings = ({
  companyId,
  onSaveSuccess,
}: {
  companyId: string;
  onSaveSuccess: (status: 'success' | 'warning' | 'error') => void;
}) => {
  const [accountCodeMappings, setAccountCodeMappings] = useState<{
    dataRef: XeroAccountCodeMappingExtendedItem[];
    data: XeroAccountCodeMappingExtendedItem[];
    dataChanged: XeroAccountCodeMappingExtendedItem[];
  }>({
    dataRef: [], // Initial data from API
    data: [], // Data to be displayed
    dataChanged: [], // Data that has been changed
  });

  /**
   * Get the account code mappings
   */
  const {
    data: fetchedAccountCodeMappings,
    isLoading: isFetchingAccountCodeMappings,
    isError: hasErrorFetchingAccountCodeMappings,
  } = useXeroAccountCodeMappings({
    clientId: companyId,
  });

  useEffect(() => {
    fetchedAccountCodeMappings &&
      setAccountCodeMappings({
        dataRef: fetchedAccountCodeMappings,
        data: fetchedAccountCodeMappings,
        dataChanged: [],
      });
  }, [fetchedAccountCodeMappings]);

  /**
   * Transform the updated data to show error messages if there are any
   * @param updatedXeroAccountCodeMappings updated xero account code mappings
   */
  const handleUpdatedAccountCodeMappings = (
    updatedXeroAccountCodeMappings: unknown[],
  ) => {
    const newItems = transformUpdatedAccountCodeMappings(
      accountCodeMappings.data,
      updatedXeroAccountCodeMappings,
    );

    const updatedDataReference = accountCodeMappings.dataRef.map(
      (itemReference) => {
        const item = newItems.find(
          (updatedItem) =>
            updatedItem.category.id === itemReference.category.id,
        );
        return !item || !!item?.error ? itemReference : item;
      },
    );

    const updatedData = accountCodeMappings.dataRef.map(
      (itemReference) =>
        newItems.find(
          (updatedItem) =>
            updatedItem.category.id === itemReference.category.id,
        ) ?? itemReference,
    );

    setAccountCodeMappings({
      dataRef: updatedDataReference,
      data: updatedData,
      dataChanged: [],
    });
  };

  /**
   * Update the account code mappings
   */
  const {
    mutate: updateAccountCodeMappings,
    isPending: isUpdatingAccountCodeMappings,
  } = useXeroAccountCodeMappingsMutation({
    clientId: companyId,
    onSuccess: (_, __, context) => {
      if (!context?.data) {
        return;
      }

      const errorItems = context.data.filter((item) => item.error);
      const totalItems = context.data.length;
      const hasErrors = errorItems.length > 0;
      const hasPartialSuccess = hasErrors && errorItems.length < totalItems;

      handleUpdatedAccountCodeMappings(context.data);

      if (!hasErrors) {
        onSaveSuccess('success');
        return;
      }

      onSaveSuccess(hasPartialSuccess ? 'warning' : 'error');
    },
  });

  /**
   * Change the xero account code of a row or all the rows in the table
   * @param newXeroAccountCode a new xero account code
   * @param rowIndex row index to update (if it is undefined, all the rows will be updated)
   */
  const changeXeroAccountCode: TableMetaType['updateXeroAccountCode'] = (
    newXeroAccountCode: XeroAccountCodeMappingExtendedItem['xeroAccountCode'],
    rowIndex?: number,
  ) => {
    setAccountCodeMappings((previousData) => {
      if (!previousData) {
        return previousData;
      }

      const updatedData = previousData.data.map((row, index) => {
        if (rowIndex === undefined || index === rowIndex) {
          return {
            ...row,
            xeroAccountCode: newXeroAccountCode,
            error: undefined, // Clear the error message when there is a change
            warning:
              row.category.taxRate === newXeroAccountCode?.taxRate
                ? undefined
                : TAX_RATE_MISMATCH_WARNING,
          };
        }
        return row;
      });

      return {
        dataRef: previousData.dataRef,
        data: updatedData,
        dataChanged:
          rowIndex === undefined
            ? updatedData
            : [...previousData.dataChanged, updatedData[rowIndex]],
      };
    });
  };

  /**
   * Save the account code mappings
   */
  const saveAccountCodeMappings = useCallback(() => {
    updateAccountCodeMappings(
      accountCodeMappings.data
        .filter(
          (item) =>
            !!item.xeroAccountCode &&
            item.xeroAccountCode !==
              accountCodeMappings.dataRef.find(
                (original) => original.category.id === item.category.id,
              )?.xeroAccountCode,
        )
        .map((item) => ({
          categoryId: item.category.id,
          xeroAccountCodeId: item.xeroAccountCode?.id ?? '',
        })),
    );
  }, [accountCodeMappings, updateAccountCodeMappings]);

  /**
   * Check if the account code mappings can be saved
   */
  const canSave = useMemo(
    () =>
      !compare(
        accountCodeMappings.dataRef.map((dataReference) => ({
          categoryId: dataReference.category.id,
          xeroAccountCodeId: dataReference.xeroAccountCode?.id,
        })),
        accountCodeMappings.data.map((data) => ({
          categoryId: data.category.id,
          xeroAccountCodeId: data.xeroAccountCode?.id,
        })),
      ) &&
      !isFetchingAccountCodeMappings &&
      !isUpdatingAccountCodeMappings &&
      accountCodeMappings.dataRef.length !==
        accountCodeMappings.data.filter((item) => item.error)?.length,
    [
      accountCodeMappings.data,
      accountCodeMappings.dataRef,
      isFetchingAccountCodeMappings,
      isUpdatingAccountCodeMappings,
    ],
  );

  return {
    accountCodeMappings,
    isFetchingAccountCodeMappings,
    isUpdatingAccountCodeMappings,
    hasErrorFetchingAccountCodeMappings,
    changeXeroAccountCode,
    saveAccountCodeMappings,
    canSave,
  };
};
